import { SortingChannels } from 'constants/sorting'
import { exists } from 'helpers/exists'
import { useObserver } from 'mobx-react-lite'
import EditorialRow from 'pages/Story/components/EditorialRow'
import React, { FC, useCallback } from 'react'
import MixedTileRow from 'sharedComponents/ProductStream/MixedTileRow'
import OutfitRow from 'sharedComponents/ProductStream/OutfitRow'
import Stream from 'sharedComponents/ProductStream/Stream'
import { useStreamIndexOffset } from 'sharedComponents/ProductStream/helpers'
import { useStoryPageStore, useUrlManagerStore } from 'store/context'
import {
  useFilterStore,
  useSortingStore,
} from 'store/sidecar/ProductStreamContext'
import EmptyStream from 'uiComponents/molecules/EmptyStream/EmptyStream'
import { RowTypes } from 'uiComponents/molecules/TileGrid/RowBuilder'
import { RowComponentMap } from 'uiComponents/molecules/TileGrid/TileGrid'
import {
  getIsEditorialPresent,
  updateInterleaveRowsAndProductTiles,
} from '../redial.helpers'

const StoryStream: FC = () => {
  const storyPageStore = useStoryPageStore()
  const filterStore = useFilterStore()
  const sortingStore = useSortingStore()
  const urlManagerStore = useUrlManagerStore()
  const queryCategory = useObserver(() => urlManagerStore.params.category)

  const isIdolPage = useObserver(
    () => exists(storyPageStore.story) && storyPageStore.story.isIdolStory,
  )
  const isIdolPageWithoutOutfits = useObserver(
    () => isIdolPage && storyPageStore.additionalTiles.length === 0,
  )
  const isEmptyProductsStream = useObserver(
    () => storyPageStore.productIds.length === 0,
  )
  const isStreamIsNotLoading = useObserver(() => !storyPageStore.streamLoading)
  const isRowBuilderEmpty = storyPageStore.rowBuilder.totalRowCount === 0

  const isEditorialPresent = getIsEditorialPresent({
    pageIsFiltered: filterStore.isFiltered,
    categoryId: queryCategory,
    sortId: sortingStore.appliedProductsSortingId,
  })

  const onTilesPerRowChanged = useCallback(() => {
    updateInterleaveRowsAndProductTiles(storyPageStore, isEditorialPresent)
  }, [storyPageStore, isEditorialPresent])

  const { productIndexOffset, rowIndexOffset } = useStreamIndexOffset(
    storyPageStore.pagination,
  )

  return useObserver(() => {
    const isEmpty =
      isStreamIsNotLoading &&
      isEmptyProductsStream &&
      isRowBuilderEmpty &&
      (!isIdolPage || isIdolPageWithoutOutfits)

    const additionalSectionsOffset =
      storyPageStore.pagination.currentPage > 1
        ? storyPageStore.storyRows.length
        : 0
    const componentMap: RowComponentMap = isIdolPage
      ? {
          [RowTypes.StreamRow]: OutfitRow,
          [RowTypes.EditorialRow]: EditorialRow,
        }
      : {
          [RowTypes.StreamRow]: MixedTileRow,
          [RowTypes.EditorialRow]: EditorialRow,
        }

    return (
      <>
        {isEmpty ? (
          <EmptyStream />
        ) : (
          <Stream
            loading={storyPageStore.streamLoading}
            rowBuilder={storyPageStore.rowBuilder}
            firstBatchProductIds={storyPageStore.productIds}
            additionalTiles={storyPageStore.additionalTiles}
            onTilesPerRowChanged={onTilesPerRowChanged}
            rowComponentMap={componentMap}
            productIndexOffset={productIndexOffset}
            rowIndexOffset={rowIndexOffset + additionalSectionsOffset}
            overwriteSortChannelForTracking={
              isEditorialPresent
                ? SortingChannels.UnSponsoredDefault
                : undefined
            }
            forceAddProducts
            excludeSponsoredBadge
            setProductCurrencyCode={(currencyCode: string) => {
              storyPageStore.setProductCurrencyCode(currencyCode)
            }}
            recoType="product_stream"
          />
        )}
      </>
    )
  })
}

export default StoryStream
